(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = Boolean(navigator.connection?.saveData);
  const automaticVideoDisabled = reduceMotion || saveData;
  const mobileQuery = window.matchMedia('(max-width: 767px)');
  const mobile = () => mobileQuery.matches;

  const analyticsPageType = () => {
    if (document.body.classList.contains('deposit-page')) return 'deposit';
    if (document.body.classList.contains('template-index')) return 'landing';
    return 'other';
  };
  const analyticsPlacement = element => {
    const explicit = element?.closest?.('[data-analytics-placement]')?.dataset.analyticsPlacement;
    if (explicit) return explicit;
    if (element?.closest?.('#AisekarNewsletterThanks')) return 'newsletter_thank_you';
    if (element?.closest?.('#AisekarReservationThanks')) return 'reservation_thank_you';
    if (element?.closest?.('.landing-mobile-offer, .deposit-mobile-order-bar')) return 'mobile_sticky';
    if (element?.closest?.('.mobile-nav-panel')) return 'mobile_menu';
    if (element?.closest?.('.topbar, .announcement')) return 'header';
    if (element?.closest?.('.capture')) return 'final';
    const moduleId = element?.closest?.('[data-module]')?.dataset.module;
    return moduleId ? `module_${moduleId}` : 'content';
  };
  const analyticsDestination = element => {
    const href = element?.getAttribute?.('href');
    if (!href) return '';
    try {
      const target = new URL(href, window.location.origin);
      return target.origin === window.location.origin ? target.pathname : `${target.origin}${target.pathname}`;
    } catch (_) {
      return href.split('?')[0].split('#')[0];
    }
  };
  const publishAnalyticsEvent = (eventName, eventData = {}) => {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return false;
    const payload = {
      schema_version: '1.0',
      page_path: window.location.pathname,
      page_type: analyticsPageType(),
      device_type: mobile() ? 'mobile' : 'desktop',
      ...eventData
    };
    try {
      return publish.call(window.Shopify.analytics, eventName, payload);
    } catch (_) {
      return false;
    }
  };

  const videoFallbacks = new WeakMap();
  const prepareInlineVideo = video => {
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  };
  const posterSourceFor = video => {
    const source = video?.dataset.poster;
    if (!source) return '';
    if (!mobile()) return source;
    try {
      const url = new URL(source, window.location.origin);
      if (url.searchParams.has('width')) url.searchParams.set('width', '960');
      return url.toString();
    } catch (_) {
      return source;
    }
  };
  const hydrateVideoPoster = video => {
    if (!video || video.poster || !video.dataset.poster) return;
    video.poster = posterSourceFor(video);
  };
  const prewarmVideo = (video, { force = false } = {}) => {
    if (!video) return;
    hydrateVideoPoster(video);
    if (!force && saveData) return;
    if (video.dataset.prewarmed === 'true') {
      if (force && video.readyState < 2) video.load();
      return;
    }
    video.dataset.prewarmed = 'true';
    video.preload = 'auto';
    video.load();
  };
  const ensureVideoFallback = video => {
    if (!video) return null;
    if (videoFallbacks.has(video)) return videoFallbacks.get(video);
    const host = video.parentElement;
    if (!host) return null;
    let button = host.querySelector('[data-video-play-fallback]');
    if (!button) {
      const icon = document.createElement('span');
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'video-play-fallback';
      button.hidden = true;
      button.setAttribute('aria-label', `Play ${video.getAttribute('aria-label') || 'video'}`);
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '▶';
      button.append(icon);
      host.append(button);
    }
    host.classList.add('video-fallback-host');
    const label = button.querySelector('[data-video-play-label]');
    if (label && !button.dataset.defaultLabel) button.dataset.defaultLabel = label.textContent || '';
    button.addEventListener('click', () => {
      if (button.classList.contains('is-unavailable')) return;
      video.dataset.userActivated = 'true';
      attemptVideoPlay(video, { userInitiated: true });
    });
    videoFallbacks.set(video, button);
    return button;
  };
  const showVideoFallback = video => {
    const button = ensureVideoFallback(video);
    if (button) button.hidden = false;
  };
  const hideVideoFallback = video => {
    const button = ensureVideoFallback(video);
    if (button) button.hidden = true;
  };
  const markVideoUnavailable = video => {
    const button = ensureVideoFallback(video);
    if (!button) return;
    const label = button.querySelector('[data-video-play-label]');
    if (label && button.dataset.errorLabel) label.textContent = button.dataset.errorLabel;
    button.classList.remove('is-loading');
    button.classList.add('is-unavailable');
    button.setAttribute('aria-disabled', 'true');
    button.hidden = false;
    delete video.dataset.userActivated;
    video.dataset.playbackState = 'unavailable';
  };
  const attemptVideoPlay = async (video, { userInitiated = false } = {}) => {
    if (!video) return false;
    prepareInlineVideo(video);
    prewarmVideo(video, { force: userInitiated });
    const button = ensureVideoFallback(video);
    if (button?.classList.contains('is-unavailable')) return false;
    if (userInitiated) button?.classList.add('is-loading');
    video.dataset.playbackState = 'attempting';
    try {
      const playback = video.play();
      await Promise.race([
        Promise.resolve(playback),
        new Promise((_, reject) => window.setTimeout(() => reject(new Error('Playback timed out')), 6000))
      ]);
      video.dataset.playbackState = 'playing';
      hideVideoFallback(video);
      return true;
    } catch (_) {
      if (userInitiated) delete video.dataset.userActivated;
      video.dataset.playbackState = 'blocked';
      showVideoFallback(video);
      return false;
    } finally {
      button?.classList.remove('is-loading');
    }
  };

  const managedVideos = [...document.querySelectorAll('video.auto-video, video.intelligence-video')];
  managedVideos.forEach(video => {
    prepareInlineVideo(video);
    ensureVideoFallback(video);
    video.addEventListener('playing', () => hideVideoFallback(video));
    video.addEventListener('error', () => markVideoUnavailable(video));
    if (automaticVideoDisabled) showVideoFallback(video);
  });

  const openDialog = dialog => {
    if (!dialog) return;
    dialog.hidden = false;
    dialog.classList.add('is-open');
    dialog.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-dialog-open');
    window.requestAnimationFrame(() => dialog.querySelector('[data-dialog-close]')?.focus());
  };
  const closeDialog = dialog => {
    if (!dialog) return;
    dialog.classList.remove('is-open');
    dialog.setAttribute('aria-hidden', 'true');
    dialog.hidden = true;
    document.body.classList.remove('has-dialog-open');
  };
  document.querySelectorAll('.thank-you-dialog').forEach(dialog => {
    dialog.querySelectorAll('[data-dialog-close]').forEach(button => button.addEventListener('click', () => closeDialog(dialog)));
    dialog.addEventListener('click', event => { if (event.target === dialog) closeDialog(dialog); });
  });
  window.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.thank-you-dialog.is-open').forEach(closeDialog);
  });
  const pageParams = new URLSearchParams(window.location.search);
  const newsletterReturned = ['customer_posted', 'contact_posted', 'newsletter_submitted']
    .some(key => pageParams.get(key) === 'true');
  let newsletterPendingAt = 0;
  try {
    newsletterPendingAt = Number(window.sessionStorage.getItem('aisekarNewsletterPendingAt')) || 0;
  } catch (_) {}
  const newsletterPending = newsletterPendingAt > 0 && Date.now() - newsletterPendingAt < 120000;
  if (document.documentElement.dataset.newsletterSuccess === 'true' || newsletterReturned || newsletterPending) {
    openDialog(document.getElementById('AisekarNewsletterThanks'));
    try { window.sessionStorage.removeItem('aisekarNewsletterPendingAt'); } catch (_) {}
    if (newsletterReturned && window.history?.replaceState) {
      ['customer_posted', 'contact_posted', 'newsletter_submitted'].forEach(key => pageParams.delete(key));
      const cleanQuery = pageParams.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${cleanQuery ? `?${cleanQuery}` : ''}${window.location.hash}`);
    }
  }
  window.AisekarThankYou = {
    newsletter: () => openDialog(document.getElementById('AisekarNewsletterThanks')),
    reservation: confirmation => {
      const dialog = document.getElementById('AisekarReservationThanks');
      if (confirmation?.number) dialog?.querySelector('[data-reservation-number]')?.replaceChildren(confirmation.number);
      if (confirmation?.email) dialog?.querySelector('[data-reservation-email]')?.replaceChildren(confirmation.email);
      openDialog(dialog);
    }
  };
  document.querySelectorAll('[data-newsletter-submit]').forEach(submitButton => {
    const form = submitButton.form;
    if (!form) return;
    form.addEventListener('submit', () => {
      if (!form.checkValidity()) return;
      publishAnalyticsEvent('newsletter_submit', {
        placement: analyticsPlacement(form),
        form_id: form.id || 'newsletter'
      });
      const status = form.querySelector('.form-status');
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting…';
      if (status) status.textContent = '';

      const returnInput = form.querySelector('[data-newsletter-return-to]');
      if (returnInput) {
        const returnParams = new URLSearchParams();
        returnParams.set('newsletter_submitted', 'true');
        const previewThemeId = pageParams.get('preview_theme_id');
        if (previewThemeId) returnParams.set('preview_theme_id', previewThemeId);
        returnInput.value = `${window.location.pathname}?${returnParams.toString()}`;
      }
      try { window.sessionStorage.setItem('aisekarNewsletterPendingAt', String(Date.now())); } catch (_) {}
    });
  });

  const previewThemeId = pageParams.get('preview_theme_id');
  if (previewThemeId) {
    document.querySelectorAll('[data-thank-you-deposit]').forEach(link => {
      const target = new URL(link.href, window.location.origin);
      if (target.origin !== window.location.origin) return;
      target.searchParams.set('preview_theme_id', previewThemeId);
      link.href = `${target.pathname}${target.search}${target.hash}`;
    });
  }

  const confirmationParams = pageParams;
  if (confirmationParams.get('reservation_success') === 'true') {
    window.AisekarThankYou.reservation({
      number: confirmationParams.get('confirmation') || 'Confirmed',
      email: confirmationParams.get('email') || 'Check your confirmation email'
    });
  }

  const menuToggle = document.querySelector('.mobile-menu-toggle');
  const menuPanel = document.querySelector('.mobile-nav-panel');
  const closeMenu = () => {
    document.querySelector('.topbar')?.classList.remove('is-menu-open');
    menuToggle?.setAttribute('aria-expanded', 'false');
    menuPanel?.setAttribute('aria-hidden', 'true');
  };
  menuToggle?.addEventListener('click', () => {
    const open = menuToggle.getAttribute('aria-expanded') !== 'true';
    document.querySelector('.topbar')?.classList.toggle('is-menu-open', open);
    menuToggle.setAttribute('aria-expanded', String(open));
    menuPanel?.setAttribute('aria-hidden', String(!open));
  });
  menuPanel?.querySelectorAll('a').forEach(link => link.addEventListener('click', closeMenu));
  window.addEventListener('keydown', event => { if (event.key === 'Escape') closeMenu(); });

  const fixedHeaderSection = document.getElementById('shopify-section-aisekar-header');
  const depositAnchorNavs = [...document.querySelectorAll('[data-deposit-anchor-nav]')];
  const getVisibleDepositAnchorNav = () => depositAnchorNavs.find(nav => window.getComputedStyle(nav).display !== 'none') || depositAnchorNavs[0];
  const getHeaderOffset = () => {
    if (!fixedHeaderSection) return 0;
    if (document.body.classList.contains('deposit-page') && window.matchMedia('(max-width: 1023px)').matches) {
      return getVisibleDepositAnchorNav()?.offsetHeight || 44;
    }
    return fixedHeaderSection.offsetHeight;
  };
  const updateFixedHeaderOffset = () => {
    if (!fixedHeaderSection || !document.body.matches('.template-index, .deposit-page')) return;
    document.documentElement.style.setProperty('--site-header-offset', `${getHeaderOffset()}px`);
  };
  updateFixedHeaderOffset();
  if ('ResizeObserver' in window && fixedHeaderSection) new ResizeObserver(updateFixedHeaderOffset).observe(fixedHeaderSection);
  window.addEventListener('resize', updateFixedHeaderOffset, { passive: true });

  if (depositAnchorNavs.length && fixedHeaderSection) {
    if ('ResizeObserver' in window) {
      const depositNavResizeObserver = new ResizeObserver(updateFixedHeaderOffset);
      depositAnchorNavs.forEach(nav => depositNavResizeObserver.observe(nav));
    }
    const anchorLinks = [...document.querySelectorAll('[data-deposit-anchor-link]')];
    const anchorTargets = [...new Set(anchorLinks
      .map(link => document.querySelector(link.hash))
      .filter(Boolean))];
    const reduceAnchorMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let activeAnchorId = '';
    let anchorFrame;

    const setActiveAnchor = targetId => {
      if (!targetId || targetId === activeAnchorId) return;
      activeAnchorId = targetId;
      anchorLinks.forEach(link => {
        const active = link.hash === `#${targetId}`;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      const visibleNav = getVisibleDepositAnchorNav();
      const visibleActiveLink = [...(visibleNav?.querySelectorAll('[data-deposit-anchor-link]') || [])]
        .find(link => link.hash === `#${targetId}`);
      if (visibleNav && visibleActiveLink && visibleNav.scrollWidth > visibleNav.clientWidth) {
        const nextLeft = visibleActiveLink.offsetLeft - ((visibleNav.clientWidth - visibleActiveLink.offsetWidth) / 2);
        visibleNav.scrollTo({ left: Math.max(0, nextLeft), behavior: reduceAnchorMotion ? 'auto' : 'smooth' });
      }
    };

    const updateDepositNavigation = () => {
      anchorFrame = undefined;
      const mobileNavOnly = window.matchMedia('(max-width: 1023px)').matches;
      const visibleNav = getVisibleDepositAnchorNav();
      const navIsPinned = visibleNav?.getBoundingClientRect().top <= 0 && window.scrollY > 0;
      fixedHeaderSection.classList.toggle('is-scrolled', mobileNavOnly ? navIsPinned : window.scrollY > 8);
      const marker = getHeaderOffset() + Math.min(120, window.innerHeight * .18);
      let currentTarget = anchorTargets[0];
      anchorTargets.forEach(target => {
        if (target.getBoundingClientRect().top <= marker) currentTarget = target;
      });
      setActiveAnchor(currentTarget?.id);
    };

    const requestDepositNavigationUpdate = () => {
      if (anchorFrame) return;
      anchorFrame = window.requestAnimationFrame(updateDepositNavigation);
    };

    anchorLinks.forEach(link => {
      link.addEventListener('click', event => {
        const target = document.querySelector(link.hash);
        if (!target) return;
        event.preventDefault();
        const targetTop = target.getBoundingClientRect().top + window.scrollY - getHeaderOffset() - 12;
        window.history.replaceState(null, '', link.hash);
        setActiveAnchor(target.id);
        window.scrollTo({ top: Math.max(0, targetTop), behavior: reduceAnchorMotion ? 'auto' : 'smooth' });
      });
    });

    window.addEventListener('scroll', requestDepositNavigationUpdate, { passive: true });
    window.addEventListener('resize', requestDepositNavigationUpdate, { passive: true });
    updateDepositNavigation();

    if (window.location.hash) {
      const initialTarget = anchorTargets.find(target => `#${target.id}` === window.location.hash);
      if (initialTarget) window.requestAnimationFrame(() => {
        const targetTop = initialTarget.getBoundingClientRect().top + window.scrollY - getHeaderOffset() - 12;
        window.scrollTo({ top: Math.max(0, targetTop), behavior: 'auto' });
      });
    }
  }

  const setupScrollRevealBar = (bar, trigger, bodyClass) => {
    if (!bar || !trigger) return;
    const revealGap = 20;
    let barVisible = false;
    let barFrame;
    const setBar = visible => {
      if (visible === barVisible) return;
      barVisible = visible;
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', String(!visible));
      document.body.classList.toggle(bodyClass, visible);
    };
    const updateBar = () => {
      barFrame = undefined;
      const triggerBottom = trigger.getBoundingClientRect().bottom;
      if (!barVisible && triggerBottom <= revealGap) setBar(true);
      else if (barVisible && triggerBottom > revealGap) setBar(false);
    };
    const requestBarUpdate = () => {
      if (barFrame) return;
      barFrame = window.requestAnimationFrame(updateBar);
    };
    updateBar();
    window.addEventListener('scroll', requestBarUpdate, { passive: true });
    window.addEventListener('resize', requestBarUpdate, { passive: true });
  };

  const mobileOrderBar = document.querySelector('.deposit-mobile-order-bar');
  const mobileOrderTrigger = document.querySelector('[data-sticky-order-trigger]');
  setupScrollRevealBar(mobileOrderBar, mobileOrderTrigger, 'has-mobile-order-bar');
  setupScrollRevealBar(document.querySelector('.landing-mobile-offer'), document.querySelector('.hero-deposit-cta'), 'has-landing-offer-bar');

  const autoVideos = [...document.querySelectorAll('video.auto-video')];
  const primaryCoreVideo = autoVideos[0];
  const visibleAutoVideos = new Set();
  if ('IntersectionObserver' in window) {
    const coreVideoWarmObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        hydrateVideoPoster(entry.target);
        if (!automaticVideoDisabled) prewarmVideo(entry.target);
        coreVideoWarmObserver.unobserve(entry.target);
      });
    }, { rootMargin: '650px 0px', threshold: 0.01 });
    managedVideos.forEach(video => coreVideoWarmObserver.observe(video));

    const warmPrimaryCoreOnIntent = () => {
      if (!primaryCoreVideo || automaticVideoDisabled || primaryCoreVideo.dataset.prewarmed === 'true') return;
      const distanceFromViewport = primaryCoreVideo.getBoundingClientRect().top - window.innerHeight;
      if (distanceFromViewport > 1600) return;
      prewarmVideo(primaryCoreVideo);
      ['scroll', 'touchstart', 'pointerdown'].forEach(name => window.removeEventListener(name, warmPrimaryCoreOnIntent));
    };
    ['scroll', 'touchstart', 'pointerdown'].forEach(name => window.addEventListener(name, warmPrimaryCoreOnIntent, { passive: true }));
  } else {
    managedVideos.forEach(video => {
      hydrateVideoPoster(video);
    });
  }
  if (!automaticVideoDisabled && 'IntersectionObserver' in window) {
    const videoObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          visibleAutoVideos.add(entry.target);
          attemptVideoPlay(entry.target);
        } else {
          const wasActive = entry.target.dataset.playbackState === 'playing' || entry.target.currentTime > 0;
          visibleAutoVideos.delete(entry.target);
          entry.target.pause();
          hideVideoFallback(entry.target);
          if (mobile() && wasActive) {
            window.setTimeout(() => {
              if (visibleAutoVideos.has(entry.target)) return;
              delete entry.target.dataset.prewarmed;
              entry.target.preload = 'none';
              entry.target.load();
            }, 800);
          }
        }
      });
    }, { rootMargin: '0px', threshold: 0.2 });
    autoVideos.forEach(video => videoObserver.observe(video));
  } else if (!automaticVideoDisabled) {
    autoVideos.forEach(video => attemptVideoPlay(video));
  }
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      autoVideos.forEach(video => video.pause());
      return;
    }
    if (!automaticVideoDisabled) {
      visibleAutoVideos.forEach(video => attemptVideoPlay(video));
    }
  });
  mobileQuery.addEventListener?.('change', () => {
    autoVideos.forEach(video => {
      if (!automaticVideoDisabled && visibleAutoVideos.has(video)) {
        video.controls = false;
        attemptVideoPlay(video);
      }
    });
  });

  document.querySelectorAll('.action-film').forEach(film => {
    const video = film.querySelector('video.action-video');
    const button = film.querySelector('[data-action-video-center-play]');
    if (!video || !button) return;
    const syncButton = () => {
      button.hidden = !video.paused && !video.ended;
    };
    button.addEventListener('click', async () => {
      if (video.ended) video.currentTime = 0;
      button.hidden = true;
      try {
        await video.play();
      } catch (_) {
        syncButton();
      }
    });
    video.addEventListener('play', syncButton);
    video.addEventListener('pause', syncButton);
    video.addEventListener('ended', syncButton);
    syncButton();
  });

  document.querySelectorAll('[data-action-cinema]').forEach(cinema => {
    const film = cinema.querySelector('.action-film');
    const copy = cinema.querySelector('.action-contained-copy');
    const update = () => {
      if (!film) return;
      cinema.classList.add('is-copy-above');
      if (mobile()) {
        film.style.removeProperty('--film-width');
        film.style.removeProperty('--film-height');
        film.style.setProperty('--film-radius', '0px');
        return;
      }
      const rect = cinema.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const copyHeight = copy?.offsetHeight || 0;
      const travel = Math.max(1, cinema.offsetHeight - viewportHeight - copyHeight);
      const progress = reduceMotion ? 1 : Math.min(1, Math.max(0, (-rect.top - copyHeight) / travel));
      const targetWidth = Math.min(960, viewportWidth - 64);
      const targetHeight = targetWidth * 9 / 16;
      film.style.setProperty('--film-width', `${viewportWidth + ((targetWidth - viewportWidth) * progress)}px`);
      film.style.setProperty('--film-height', `${viewportHeight + ((targetHeight - viewportHeight) * progress)}px`);
      film.style.setProperty('--film-radius', `${progress * 16}px`);
    };
    update();
    if (!reduceMotion) {
      let frame;
      window.addEventListener('scroll', () => {
        if (frame) return;
        frame = requestAnimationFrame(() => { update(); frame = undefined; });
      }, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    }
  });

  const createCarousel = ({ root, cardSelector, jumpSelector, interval = 4500, mobileOnly = true, autoplay = true, onActivate }) => {
    const container = document.querySelector(root);
    if (!container) return;
    const cards = [...container.querySelectorAll(cardSelector)];
    const jumps = [...container.querySelectorAll(jumpSelector)];
    if (!cards.length) return;
    let index = 0;
    let timer;
    let visible = false;
    const stop = () => { window.clearInterval(timer); timer = undefined; };
    const activate = (next, restart = false) => {
      index = (next + cards.length) % cards.length;
      cards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === index));
      jumps.forEach(button => {
        const value = button.dataset.carouselJump || button.dataset.processJump || button.dataset.appJump;
        const active = !['previous', 'next'].includes(value) && Number(value) === index;
        button.classList.toggle('is-active', active);
        if (!['previous', 'next'].includes(value)) button.setAttribute('aria-selected', String(active));
        button.classList.remove('is-running');
        if (active && autoplay && visible && !reduceMotion) requestAnimationFrame(() => button.classList.add('is-running'));
      });
      onActivate?.(index);
      if (restart && visible && !reduceMotion) start();
    };
    const start = () => {
      stop();
      if (!autoplay) return;
      if (mobileOnly && !mobile()) return;
      timer = window.setInterval(() => activate(index + 1), interval);
    };
    jumps.forEach(button => button.addEventListener('click', () => {
      const value = button.dataset.carouselJump || button.dataset.processJump || button.dataset.appJump;
      if (value === 'previous') activate(index - 1, true);
      else if (value === 'next') activate(index + 1, true);
      else activate(Number(value), true);
    }));
    activate(0);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting);
        if (visible && autoplay && !reduceMotion) start(); else stop();
      }, { threshold: 0.25 }).observe(container);
    }
  };

  document.querySelectorAll('.process-breakdown').forEach((_, instanceIndex) => {
    const selector = `.process-breakdown:nth-of-type(${instanceIndex + 1})`;
    const container = document.querySelectorAll('.process-breakdown')[instanceIndex];
    container.dataset.carouselInstance = String(instanceIndex);
    createCarousel({ root: `[data-carousel-instance="${instanceIndex}"]`, cardSelector: '.process-step', jumpSelector: '[data-process-jump]', autoplay: false });
  });
  createCarousel({ root: '.app-console', cardSelector: '.app-flow-item', jumpSelector: '[data-app-jump]' });

  document.querySelectorAll('[data-intelligence-core]').forEach(core => {
    const items = [...core.querySelectorAll('[data-intelligence-index]')];
    const panels = [...core.querySelectorAll('[data-intelligence-panel]')];
    const jumps = [...core.querySelectorAll('[data-intelligence-jump]')];
    const videos = panels.map(panel => panel.querySelector('video'));
    const progress = core.querySelector('[data-intelligence-progress]');
    let index = 0;
    let visible = false;
    const activeDuration = () => {
      const duration = videos[index]?.duration;
      return Number.isFinite(duration) && duration > 0 ? duration : 8;
    };
    const restartProgress = () => {
      const duration = activeDuration();
      core.style.setProperty('--intelligence-duration', `${duration}s`);
      jumps.forEach(button => button.classList.remove('is-running'));
      progress?.classList.remove('is-running');
      if (!visible || automaticVideoDisabled) return;
      window.requestAnimationFrame(() => {
        jumps.forEach(button => {
          if (button.classList.contains('is-active')) button.classList.add('is-running');
        });
        progress?.classList.add('is-running');
      });
    };
    const activate = (next, restartMedia = false) => {
      index = (next + items.length) % items.length;
      items.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-expanded', String(active));
        const toggle = item.querySelector('.intelligence-toggle');
        if (toggle) toggle.textContent = active ? '×' : '+';
      });
      core.querySelector('[data-intelligence-items]')?.classList.toggle('is-second-active', index === 1);
      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
        const video = videos[panelIndex];
        if (active && restartMedia && video) video.currentTime = 0;
        if (active && visible && !automaticVideoDisabled) attemptVideoPlay(video);
        else video?.pause();
      });
      jumps.forEach(button => {
        const value = button.dataset.intelligenceJump;
        const active = !['previous', 'next'].includes(value) && Number(value) === index;
        button.classList.toggle('is-active', active);
        if (!['previous', 'next'].includes(value)) button.setAttribute('aria-selected', String(active));
      });
      restartProgress();
    };
    items.forEach(item => item.addEventListener('click', () => activate(Number(item.dataset.intelligenceIndex), true)));
    jumps.forEach(button => button.addEventListener('click', () => {
      const value = button.dataset.intelligenceJump;
      if (value === 'previous') activate(index - 1, true);
      else if (value === 'next') activate(index + 1, true);
      else activate(Number(value), true);
    }));
    videos.forEach((video, videoIndex) => {
      video?.addEventListener('loadedmetadata', () => {
        if (videoIndex === index) restartProgress();
      });
      video?.addEventListener('ended', () => {
        if (visible && videoIndex === index && !automaticVideoDisabled) activate(index + 1, true);
      });
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting);
        if (visible && !automaticVideoDisabled) activate(index, true);
        else {
          videos.forEach(video => video?.pause());
          jumps.forEach(button => button.classList.remove('is-running'));
          progress?.classList.remove('is-running');
          if (visible) showVideoFallback(videos[index]);
        }
      }, { threshold: 0.35 }).observe(core);
    }
  });

  const evidenceRail = document.querySelector('#field-evidence-rail');
  const evidenceClips = [...document.querySelectorAll('.evidence-clip')];
  evidenceClips.forEach(clip => {
    const video = clip.querySelector('video');
    const button = clip.querySelector('.clip-play');
    if (!video || !button) return;
    const playLabel = button.getAttribute('aria-label') || 'Play yard video';
    prepareInlineVideo(video);
    clip.addEventListener('pointerdown', () => {
      hydrateVideoPoster(video);
      prewarmVideo(video, { force: true });
    }, { passive: true });
    button.addEventListener('click', async () => {
      if (!video.paused) {
        video.pause();
        return;
      }
      clip.closest('.evidence-rail')?.setAttribute('data-autoplay-stopped', 'true');
      document.querySelectorAll('.evidence-clip video').forEach(other => { if (other !== video) other.pause(); });
      clip.classList.add('is-loading');
      button.disabled = true;
      try {
        prewarmVideo(video, { force: true });
        await video.play();
      } catch (_) {
        clip.classList.remove('is-playing');
        button.textContent = '▶';
        button.setAttribute('aria-label', playLabel);
      } finally {
        clip.classList.remove('is-loading');
        button.disabled = false;
      }
    });
    video.addEventListener('playing', () => {
      clip.classList.add('is-playing');
      button.textContent = 'Ⅱ';
      button.setAttribute('aria-label', playLabel.replace(/^Play/i, 'Pause'));
    });
    video.addEventListener('pause', () => {
      clip.classList.remove('is-playing');
      button.textContent = '▶';
      button.setAttribute('aria-label', playLabel);
    });
    video.addEventListener('ended', () => {
      clip.classList.remove('is-playing');
      button.textContent = '▶';
      button.setAttribute('aria-label', playLabel);
    });
  });

  let evidenceWarmFrame;
  let evidenceWarmInFlight;
  let evidenceSectionIsNear = false;
  const queueEvidenceWarm = video => {
    if (!video || saveData || video.readyState >= 3) return;
    if (evidenceWarmInFlight && evidenceWarmInFlight !== video) return;
    evidenceWarmInFlight = video;
    hydrateVideoPoster(video);
    prewarmVideo(video);
    const release = () => {
      if (evidenceWarmInFlight !== video) return;
      evidenceWarmInFlight = undefined;
      requestEvidenceWarm();
    };
    video.addEventListener('canplay', release, { once: true });
    video.addEventListener('error', release, { once: true });
    window.setTimeout(release, 8000);
  };
  const prewarmNearbyEvidence = () => {
    evidenceWarmFrame = undefined;
    if (!evidenceRail || saveData || !evidenceSectionIsNear || evidenceWarmInFlight) return;
    const railRect = evidenceRail.getBoundingClientRect();
    const nextClip = evidenceClips.find(clip => {
      const clipRect = clip.getBoundingClientRect();
      if (clipRect.right < railRect.left - 24 || clipRect.left > railRect.right) return false;
      const video = clip.querySelector('video');
      return video && video.readyState < 3;
    });
    if (nextClip) queueEvidenceWarm(nextClip.querySelector('video'));
  };
  const requestEvidenceWarm = () => {
    if (!evidenceWarmFrame) evidenceWarmFrame = window.requestAnimationFrame(prewarmNearbyEvidence);
  };
  if (evidenceRail && 'IntersectionObserver' in window) {
    const evidenceSectionWarmObserver = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      evidenceSectionIsNear = true;
      requestEvidenceWarm();
      evidenceSectionWarmObserver.disconnect();
    }, { rootMargin: '700px 0px', threshold: 0.01 });
    evidenceSectionWarmObserver.observe(evidenceRail);
  } else {
    evidenceClips.slice(0, 2).forEach(clip => hydrateVideoPoster(clip.querySelector('video')));
  }
  evidenceRail?.addEventListener('scroll', requestEvidenceWarm, { passive: true });

  if (primaryCoreVideo && !saveData) {
    primaryCoreVideo.addEventListener('playing', () => {
      window.setTimeout(() => {
        const firstEvidenceVideo = document.querySelector('.evidence-clip video');
        queueEvidenceWarm(firstEvidenceVideo);
      }, 1500);
    }, { once: true });
  }

  if (evidenceRail && !reduceMotion) {
    let direction = 1;
    let paused = false;
    let inView = !('IntersectionObserver' in window);
    let previous = performance.now();
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        inView = entries.some(entry => entry.isIntersecting);
      }, { threshold: 0.05 }).observe(evidenceRail);
    }
    const tick = time => {
      const elapsed = Math.min(32, time - previous); previous = time;
      if (inView && !paused && evidenceRail.dataset.autoplayStopped !== 'true') {
        const limit = evidenceRail.scrollWidth - evidenceRail.clientWidth;
        if (limit > 2) {
          if (evidenceRail.scrollLeft >= limit - 1) direction = -1;
          if (evidenceRail.scrollLeft <= 1) direction = 1;
          evidenceRail.scrollLeft += direction * elapsed * 0.048;
        }
      }
      requestAnimationFrame(tick);
    };
    ['mouseenter', 'focusin', 'pointerdown'].forEach(name => evidenceRail.addEventListener(name, () => { paused = true; }, { passive: true }));
    ['mouseleave', 'focusout'].forEach(name => evidenceRail.addEventListener(name, () => {
      if (!evidenceRail.querySelector('.is-playing') && evidenceRail.dataset.autoplayStopped !== 'true') paused = false;
    }));
    requestAnimationFrame(tick);
  }

  const comparison = document.querySelector('.comparison-shell');
  const positionComparison = () => { if (comparison && mobile()) comparison.scrollLeft = 170; };
  positionComparison();

  const galleryTrack = document.querySelector('[data-gallery-track]');
  const gallerySlides = [...document.querySelectorAll('[data-gallery-slide]')];
  const galleryThumbs = [...document.querySelectorAll('.gallery-thumb')];
  const galleryThumbRail = document.querySelector('.purchase-thumbs');
  const galleryCounter = document.querySelector('[data-gallery-count]');
  let activeGalleryIndex = 0;
  let galleryScrollFrame;
  let galleryScrollEndTimer;
  let galleryProgrammaticScroll = false;
  const publishGalleryInteraction = (interaction, index) => {
    publishAnalyticsEvent('deposit_gallery_interaction', {
      placement: 'main_purchase',
      interaction,
      image_index: Number.isFinite(index) ? index + 1 : null
    });
  };
  const keepGalleryThumbVisible = (index, behavior = 'smooth') => {
    const thumb = galleryThumbs[index];
    if (!galleryThumbRail || !thumb || galleryThumbRail.scrollWidth <= galleryThumbRail.clientWidth + 2) return;
    const centeredLeft = thumb.offsetLeft - ((galleryThumbRail.clientWidth - thumb.offsetWidth) / 2);
    galleryThumbRail.scrollTo({
      left: Math.max(0, centeredLeft),
      behavior: reduceMotion ? 'auto' : behavior
    });
  };
  const syncGalleryState = (index, thumbBehavior = 'smooth') => {
    if (!gallerySlides.length) return;
    const normalizedIndex = Math.max(0, Math.min(gallerySlides.length - 1, index));
    activeGalleryIndex = normalizedIndex;
    galleryThumbs.forEach(item => {
      item.classList.remove('is-active');
      item.setAttribute('aria-pressed', 'false');
    });
    galleryThumbs[normalizedIndex]?.classList.add('is-active');
    galleryThumbs[normalizedIndex]?.setAttribute('aria-pressed', 'true');
    gallerySlides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === normalizedIndex));
    if (galleryCounter) galleryCounter.textContent = String(normalizedIndex + 1);
    keepGalleryThumbVisible(normalizedIndex, thumbBehavior);
  };
  const selectGalleryImage = (index, behavior = mobile() ? 'smooth' : 'auto') => {
    if (!galleryTrack || !gallerySlides.length) return;
    const normalizedIndex = (index + gallerySlides.length) % gallerySlides.length;
    galleryProgrammaticScroll = true;
    syncGalleryState(normalizedIndex, behavior);
    galleryTrack.scrollTo({
      left: gallerySlides[normalizedIndex].offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : behavior
    });
    window.clearTimeout(galleryScrollEndTimer);
    galleryScrollEndTimer = window.setTimeout(() => { galleryProgrammaticScroll = false; }, behavior === 'smooth' ? 420 : 0);
  };
  galleryThumbs.forEach((thumb, index) => thumb.addEventListener('click', () => {
    publishGalleryInteraction('thumbnail', index);
    selectGalleryImage(index);
  }));
  document.querySelector('[data-gallery-prev]')?.addEventListener('click', () => {
    const nextIndex = (activeGalleryIndex - 1 + gallerySlides.length) % gallerySlides.length;
    publishGalleryInteraction('previous', nextIndex);
    selectGalleryImage(nextIndex);
  });
  document.querySelector('[data-gallery-next]')?.addEventListener('click', () => {
    const nextIndex = (activeGalleryIndex + 1) % gallerySlides.length;
    publishGalleryInteraction('next', nextIndex);
    selectGalleryImage(nextIndex);
  });

  if (galleryTrack && gallerySlides.length > 1) {
    const updateGalleryFromScroll = () => {
      galleryScrollFrame = undefined;
      const slideWidth = galleryTrack.clientWidth || 1;
      const nextIndex = Math.max(0, Math.min(gallerySlides.length - 1, Math.round(galleryTrack.scrollLeft / slideWidth)));
      if (!galleryProgrammaticScroll && nextIndex !== activeGalleryIndex) syncGalleryState(nextIndex);
    };
    galleryTrack.addEventListener('scroll', () => {
      if (!galleryScrollFrame) galleryScrollFrame = window.requestAnimationFrame(updateGalleryFromScroll);
      window.clearTimeout(galleryScrollEndTimer);
      galleryScrollEndTimer = window.setTimeout(() => {
        updateGalleryFromScroll();
        if (!galleryProgrammaticScroll) publishGalleryInteraction('swipe', activeGalleryIndex);
        galleryProgrammaticScroll = false;
      }, 140);
    }, { passive: true });
    new ResizeObserver(() => selectGalleryImage(activeGalleryIndex, 'auto')).observe(galleryTrack);
  }

  const initializeProgressiveFaq = root => {
    root.querySelectorAll('[data-faq-progressive]').forEach(faq => {
      if (faq.dataset.faqReady === 'true') return;
      const button = faq.querySelector('[data-faq-more]');
      const buttonWrap = faq.querySelector('[data-faq-more-wrap]');
      const items = [...faq.querySelectorAll('[data-faq-item]')];
      if (!button || !buttonWrap || !items.some(item => item.hidden)) return;
      faq.dataset.faqReady = 'true';
      button.addEventListener('click', () => {
        const step = Math.max(1, Number.parseInt(button.dataset.faqStep || '5', 10));
        const nextItems = items.filter(item => item.hidden).slice(0, step);
        nextItems.forEach(item => {
          item.hidden = false;
          item.classList.add('is-revealed');
        });
        const allShown = !items.some(item => item.hidden);
        button.setAttribute('aria-expanded', String(allShown));
        if (allShown) buttonWrap.hidden = true;
      });
    });
  };
  initializeProgressiveFaq(document);
  document.addEventListener('shopify:section:load', event => initializeProgressiveFaq(event.target));

  const reservationForms = [...document.querySelectorAll('[data-vip-reservation-form]')];
  let reservationPending = false;
  reservationForms.forEach(form => {
    const button = form.querySelector('button[type="submit"]');
    if (button) button.dataset.initialDisabled = String(button.disabled);
  });
  const setReservationPending = pending => {
    reservationPending = pending;
    reservationForms.forEach(form => {
      const button = form.querySelector('button[type="submit"]');
      if (!button) return;
      button.disabled = pending || button.dataset.initialDisabled === 'true';
      if (pending) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  };
  const shopRoot = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const fetchCartJson = async (path, options = {}) => {
    const response = await fetch(`${shopRoot}${path}`, {
      credentials: 'same-origin',
      ...options,
      headers: { Accept: 'application/json', ...(options.headers || {}) }
    });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  };
  const setReservationVariantQuantity = async variantId => {
    const cart = await fetchCartJson('cart.js', { cache: 'no-store' });
    const matchingItems = (cart.items || []).filter(item => String(item.variant_id) === String(variantId));
    if (!matchingItems.length) {
      await fetchCartJson('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      return;
    }
    if (matchingItems.length === 1 && matchingItems[0].quantity === 1) return;
    const updates = {};
    matchingItems.forEach((item, index) => { updates[item.key] = index === 0 ? 1 : 0; });
    await fetchCartJson('cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
  };

  reservationForms.forEach(form => {
    form.addEventListener('submit', async event => {
      publishAnalyticsEvent('deposit_reserve_click', {
        placement: form.hasAttribute('data-vip-direct-checkout') ? 'mobile_sticky' : 'main_purchase',
        variant_id: form.querySelector('input[name="id"]')?.value || ''
      });
      if (!window.fetch) return;
      event.preventDefault();
      if (reservationPending) return;
      const variantId = form.querySelector('input[name="id"]')?.value;
      const status = form.querySelector('[role="status"]');
      if (!variantId) return;
      if (status) { status.textContent = ''; status.classList.add('visually-hidden'); }
      setReservationPending(true);
      try {
        await setReservationVariantQuantity(variantId);
        window.location.assign(`${shopRoot}checkout`);
      } catch (error) {
        if (status) { status.textContent = 'We could not start checkout. Please try again.'; status.classList.remove('visually-hidden'); }
        setReservationPending(false);
      }
    });
  });
  window.addEventListener('pageshow', () => setReservationPending(false));

  document.addEventListener('click', event => {
    const source = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!source) return;

    const trackedCta = source.closest('[data-analytics-event]');
    if (trackedCta) {
      publishAnalyticsEvent(trackedCta.dataset.analyticsEvent, {
        placement: analyticsPlacement(trackedCta),
        cta_text: (trackedCta.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 120),
        destination: analyticsDestination(trackedCta)
      });
    }

    const kickstarterLink = source.closest('a[href*="kickstarter.com"], [data-thank-you-kickstarter]');
    if (kickstarterLink && trackedCta !== kickstarterLink) {
      publishAnalyticsEvent('kickstarter_click', {
        placement: analyticsPlacement(kickstarterLink),
        cta_text: (kickstarterLink.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 120),
        destination: analyticsDestination(kickstarterLink)
      });
    }

    const faqSummary = source.closest('.faq summary');
    const faqItem = faqSummary?.closest('details');
    if (faqSummary && faqItem && !faqItem.open) {
      publishAnalyticsEvent('faq_open', {
        placement: analyticsPlacement(faqSummary),
        question: (faqSummary.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 180)
      });
    }

    const playButton = source.closest('.video-play-fallback, .clip-play, .action-video-center-play');
    if (playButton) {
      const host = playButton.closest('.video-fallback-host, .evidence-clip, .action-film') || playButton.parentElement;
      const video = host?.querySelector('video');
      if (!video || video.paused) {
        publishAnalyticsEvent('product_video_play', {
          placement: analyticsPlacement(playButton),
          video_label: (video?.getAttribute('aria-label') || playButton.getAttribute('aria-label') || 'video').slice(0, 180)
        });
      }
    }
  }, { capture: true });

  window.addEventListener('resize', positionComparison, { passive: true });
})();
